import { action } from '@ember/object';
import Route from '@ember/routing/route';
import type PublicTransition from '@ember/routing/transition';
import { service } from '@ember/service';

import type { Service } from 'webapp-types';
import { isNumber } from 'webapp/utils/number';

import type MetricLibraryController from 'webapp/controllers/metric-library';

import type { Metric } from 'webapp/models-custom';

export default class MetricLibraryRoute extends Route {
	@service('model-manager/goals') declare mmGoalsService: Service.ModelManager.Goals;
	@service('model-manager/custom-queries')
	declare mmCustomQueriesService: Service.ModelManager.CustomQueries;
	@service('model-manager/scripts') declare mmScriptsService: Service.ModelManager.Scripts;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('model-manager/templates') declare mmTemplatesService: Service.ModelManager.Templates;
	@service('account') declare accountService: Service.Account;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;

	override beforeModel(): Promise<unknown> {
		document.body.id = 'page-metrics-library';

		// dont block - but will need for metric popup (if favorite / goal added already)
		void this.mmGoalsService.requestSpaceGoals();

		// dont block - but will need for source landing pages
		void this.mmTemplatesService.requestBoardPublicTemplates();
		void this.mmTemplatesService.requestBoardLoopPublicTemplates();
		void this.mmTemplatesService.requestReportPublicTemplates();

		return Promise.all([
			this.navigationService.initialRequestsPromise,
			this.mmMetricsService.prepareForMetricEditing(),
		]);
	}

	override resetController(
		controller: MetricLibraryController,
		isExiting: boolean,
		transition: PublicTransition,
	): void {
		const index = transition?.targetName?.indexOf('metric-library');
		if (isExiting && index && index > 0) {
			controller.set('submitInProgress', false);
		}
	}

	@action
	changeSource(sourceKey: string): void {
		if (isNumber(sourceKey)) {
			const source = this.mmSourcesService.findModel(sourceKey);
			const genericKey = source?.sourceType.key || 'Custom';
			this.navigationService.transitionToRoute(
				'metric-library.source.specific',
				genericKey,
				sourceKey,
			);
		} else {
			this.navigationService.transitionToRoute('metric-library.source', sourceKey);
		}
	}

	@action
	addMetricsToBoard(selectedMetrics: Metric[]): Promise<void> | void {
		if (this.accountService.handleDemo()) {
			return;
		}

		const metricsLibraryController = this.controllerFor(
			'metric-library',
		) as MetricLibraryController;

		if (metricsLibraryController.submitInProgress) {
			return;
		}

		metricsLibraryController.submitInProgress = true;

		return this.mmMetricsService.addMetricsToBoard(selectedMetrics).finally(() => {
			metricsLibraryController.submitInProgress = false;
		});
	}
}
